import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { toast } from 'sonner';
import { Webhook, Key, Send, RefreshCw, Trash2, CheckCircle, XCircle, Clock, Copy, Eye, EyeOff, ExternalLink } from 'lucide-react';

export const ATSIntegrationPanel = () => {
  const [config, setConfig] = useState({ webhook_url: '', webhook_enabled: false, api_key: null });
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showDocs, setShowDocs] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, logsRes] = await Promise.all([
        api.get('/ats/config'),
        api.get('/ats/webhook-logs')
      ]);
      setConfig(configRes.data);
      setWebhookUrl(configRes.data.webhook_url || '');
      setWebhookEnabled(configRes.data.webhook_enabled || false);
      setLogs(logsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement ATS');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWebhook = async () => {
    setSaving(true);
    try {
      const res = await api.put('/ats/config', { webhook_url: webhookUrl, webhook_enabled: webhookEnabled });
      setConfig(res.data);
      toast.success('Configuration webhook sauvegardée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    setTesting(true);
    try {
      const res = await api.post('/ats/test-webhook');
      if (res.data.success) {
        toast.success(`Webhook envoyé (HTTP ${res.data.status_code})`);
      } else {
        toast.error(`Échec du webhook (${res.data.response_body})`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setTesting(false);
    }
  };

  const handleGenerateApiKey = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/ats/generate-api-key');
      setConfig(prev => ({ ...prev, api_key: res.data.api_key }));
      setShowApiKey(true);
      toast.success('Clé API générée');
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevokeApiKey = async () => {
    try {
      await api.delete('/ats/revoke-api-key');
      setConfig(prev => ({ ...prev, api_key: null }));
      toast.success('Clé API révoquée');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  const baseUrl = process.env.REACT_APP_BACKEND_URL;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-400">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Webhook Configuration */}
      <div className="eva-card" data-testid="webhook-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-500/15 rounded-lg flex items-center justify-center">
            <Webhook size={20} className="text-[#3B82F6]" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Webhook sortant</h3>
            <p className="text-sm text-slate-400">Envoi automatique des résultats de test vers votre ATS</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="eva-label">URL du webhook</label>
            <input type="url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} className="eva-input" placeholder="https://votre-ats.com/api/webhooks/eva" data-testid="webhook-url-input" />
          </div>
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" checked={webhookEnabled} onChange={(e) => setWebhookEnabled(e.target.checked)} className="sr-only peer" data-testid="webhook-enabled-toggle" />
              <div className="w-11 h-6 bg-[#E9ECEF] peer-focus:ring-2 peer-focus:ring-[#0B132B]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-[#0B132B] after:border after:border-white/10 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#10B981]"></div>
            </label>
            <span className="text-sm text-slate-300">{webhookEnabled ? 'Activé — envoi à chaque test terminé' : 'Désactivé'}</span>
          </div>
          <div className="flex gap-3">
            <button onClick={handleSaveWebhook} disabled={saving} className="eva-btn-primary flex items-center gap-2" data-testid="save-webhook-button">
              {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
            {webhookUrl && (
              <button onClick={handleTestWebhook} disabled={testing} className="eva-btn-secondary flex items-center gap-2" data-testid="test-webhook-button">
                <Send size={16} />
                {testing ? 'Envoi...' : 'Tester'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* API Key */}
      <div className="eva-card" data-testid="api-key-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-amber-500/15 rounded-lg flex items-center justify-center">
            <Key size={20} className="text-[#F59E0B]" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Clé API externe</h3>
            <p className="text-sm text-slate-400">Permet à votre ATS de récupérer les résultats via API REST</p>
          </div>
        </div>
        {config.api_key ? (
          <div className="space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <code className="text-sm text-white font-mono truncate flex-1 min-w-0" data-testid="api-key-display">
                  {showApiKey ? config.api_key : '••••••••••••••••••••••••••••••••'}
                </code>
                <div className="flex gap-2 shrink-0 ml-3">
                  <button onClick={() => setShowApiKey(!showApiKey)} className="p-2 hover:bg-[#0B132B] rounded transition-colors" data-testid="toggle-api-key-visibility">
                    {showApiKey ? <EyeOff size={16} className="text-slate-400" /> : <Eye size={16} className="text-slate-400" />}
                  </button>
                  <button onClick={() => copyToClipboard(config.api_key)} className="p-2 hover:bg-[#0B132B] rounded transition-colors" data-testid="copy-api-key-button">
                    <Copy size={16} className="text-slate-400" />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={handleGenerateApiKey} disabled={generating} className="eva-btn-secondary flex items-center gap-2" data-testid="regenerate-api-key-button">
                <RefreshCw size={16} /> Régénérer
              </button>
              <button onClick={handleRevokeApiKey} className="px-4 py-2 text-sm text-[#EF4444] border border-[#EF4444]/30 rounded-lg hover:bg-[#EF4444]/5 transition-colors flex items-center gap-2" data-testid="revoke-api-key-button">
                <Trash2 size={16} /> Révoquer
              </button>
            </div>
          </div>
        ) : (
          <button onClick={handleGenerateApiKey} disabled={generating} className="eva-btn-primary flex items-center gap-2" data-testid="generate-api-key-button">
            <Key size={16} /> {generating ? 'Génération...' : 'Générer une clé API'}
          </button>
        )}
      </div>

      {/* API Documentation */}
      <div className="eva-card" data-testid="api-docs-section">
        <button onClick={() => setShowDocs(!showDocs)} className="flex items-center justify-between w-full" data-testid="toggle-docs-button">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <ExternalLink size={20} className="text-white" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-white">Documentation API</h3>
              <p className="text-sm text-slate-400">Endpoints disponibles pour votre ATS</p>
            </div>
          </div>
          <span className={`text-slate-400 transition-transform ${showDocs ? 'rotate-180' : ''}`}>&#9660;</span>
        </button>
        {showDocs && (
          <div className="mt-6 space-y-4">
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Authentification</p>
              <p className="text-sm text-slate-300 mb-2">Ajoutez la clé API dans le header de chaque requête :</p>
              <code className="text-xs bg-[#0B132B] border border-white/10 rounded px-3 py-2 block text-white">X-API-Key: votre_cle_api</code>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Lister les tests</p>
              <code className="text-xs bg-[#0B132B] border border-white/10 rounded px-3 py-2 block text-white mb-2">GET {baseUrl}/api/ats/external/tests?limit=50&offset=0&ref_ao=...&client=...</code>
              <p className="text-xs text-slate-400">Retourne la liste des tests terminés avec score, évaluation, candidat.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Détail d'un test</p>
              <code className="text-xs bg-[#0B132B] border border-white/10 rounded px-3 py-2 block text-white mb-2">{"GET " + baseUrl + "/api/ats/external/tests/{test_id}"}</code>
              <p className="text-xs text-slate-400">Retourne le détail complet : questions, choix, réponses du candidat, scores.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Webhook (événement sortant)</p>
              <p className="text-sm text-slate-300 mb-2">Eva envoie un POST à votre URL à chaque test terminé. Payload :</p>
              <pre className="text-xs bg-[#0B132B] border border-white/10 rounded px-3 py-2 block text-white overflow-x-auto">
{`{
  "event": "test_completed",
  "timestamp": "2026-01-15T10:30:00Z",
  "test": {
    "id": "...",
    "candidate": { "first_name": "...", "last_name": "..." },
    "position": "Développeur Full Stack",
    "level": "Senior",
    "average_score": 4.2,
    "evaluation": "Au-dessus de la moyenne",
    "scores": [5, 4, 3.5, 4, 4.5]
  }
}`}
              </pre>
            </div>
            <div className="bg-white/5 border border-[#0B132B]/15 rounded-lg p-4">
              <p className="text-xs text-white uppercase tracking-wider font-semibold mb-2">Boond Manager — Bouton "Faire passer un test"</p>
              <p className="text-sm text-slate-300 mb-3">Configurez un bouton personnalisé dans chaque fiche candidat Boond Manager :</p>
              <ol className="text-sm text-slate-300 space-y-2 list-decimal list-inside mb-3">
                <li>Dans Boond Manager, allez dans <strong>Configuration &gt; Personnalisation</strong></li>
                <li>Ajoutez une <strong>action personnalisée</strong> de type <strong>Lien externe</strong> sur l'entité <strong>Candidat</strong></li>
                <li>Nommez-la <strong>"Faire passer un test technique"</strong></li>
                <li>Collez l'URL ci-dessous comme cible du lien</li>
              </ol>
              <p className="text-xs text-slate-400 mb-2">URL à configurer (remplacez les variables Boond) :</p>
              <div className="relative">
                <code className="text-xs bg-[#0B132B] border border-white/10 rounded px-3 py-2 block text-white break-all" data-testid="boond-url-template">
                  {baseUrl}/lancer-test-externe?nom={"{{candidate.lastName}}"}&prenom={"{{candidate.firstName}}"}&boond_id={"{{candidate.id}}"}
                </code>
                <button onClick={() => copyToClipboard(`${baseUrl}/lancer-test-externe?nom={{candidate.lastName}}&prenom={{candidate.firstName}}&boond_id={{candidate.id}}`)} className="absolute top-1.5 right-2 p-1 hover:bg-white/5 rounded">
                  <Copy size={14} className="text-slate-400" />
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-2">Le recruteur doit être connecté à Eva. Le nom et prénom du candidat seront pré-remplis automatiquement.</p>
            </div>
          </div>
        )}
      </div>

      {/* Webhook Logs */}
      <div className="eva-card" data-testid="webhook-logs-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-500/15 rounded-lg flex items-center justify-center">
              <Clock size={20} className="text-slate-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white">Historique des envois</h3>
              <p className="text-sm text-slate-400">{logs.length} dernier(s) envoi(s)</p>
            </div>
          </div>
          <button onClick={fetchData} className="p-2 hover:bg-white/5 rounded transition-colors" data-testid="refresh-logs-button">
            <RefreshCw size={16} className="text-slate-400" />
          </button>
        </div>
        {logs.length === 0 ? (
          <div className="text-center py-8 border border-white/10 rounded-lg">
            <Webhook size={32} className="text-[#E9ECEF] mx-auto mb-2" />
            <p className="text-sm text-slate-400">Aucun envoi webhook pour le moment</p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => (
              <div key={log.id} className="flex items-center justify-between p-3 border border-white/10 rounded-md" data-testid={`webhook-log-${log.id}`}>
                <div className="flex items-center gap-3">
                  {log.success ? <CheckCircle size={18} className="text-[#10B981] shrink-0" /> : <XCircle size={18} className="text-[#EF4444] shrink-0" />}
                  <div>
                    <p className="text-sm font-medium text-white">{log.event === 'test_completed' ? 'Test terminé' : 'Test de connexion'}</p>
                    <p className="text-xs text-slate-400">{new Date(log.created_at).toLocaleString('fr-FR')} — HTTP {log.status_code || 'Erreur'}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${log.success ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`}>
                  {log.success ? 'OK' : 'Échec'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
